<template>
  <div class="container py-5">
    <header class="title is-5">
      Production Task Management
    </header>

    <div 
      class="tabs" 
      v-if="workstations.length > 0">
      <ul>
        <li 
          v-for="workstation in workstations"
          :key="workstation.id"
          @click="setFilter({
            field: 'workstation_id', 
            value: workstation.id
          })"
          :class="{ 'is-active': workstation.id == filters.workstation_id }">
          <a>{{ workstation.workstation_name }}</a>
        </li>
      </ul>
    </div>

    <div
      v-if="workstations.length > 0 && workstation" 
      class="columns mb-3 mx-0 has-background-light">
      <div class="column is-one-third has-text-centered">
        <div class="has-text-weight-semibold is-size-5">
          {{ workstation.capacity_minutes | round(0) }}
        </div>
        <div>
          Daily Capacity (minutes)
        </div>
      </div>
      <div class="column is-one-third has-text-centered">
        <div class="has-text-weight-semibold is-size-5">
          {{ workstation.queued_minutes | round(0) }}
        </div>
        <div>
          Minutes Queued
        </div>
      </div>
      <div class="column is-one-third has-text-centered">
        <div class="has-text-weight-semibold is-size-5"> 
          {{ workstation.queued_minutes / workstation.capacity_minutes | round(1) }}
        </div>
        <div>
          Days Occupied
        </div>
      </div>
    </div>

    <article 
      v-if="!loading && tasks.length == 0" 
      class="message is-info is-fullwidth">
      <div class="message-body">
        There are no tasks queued for this workstation.
      </div>
    </article>

    <table 
      v-if="!loading && tasks.length > 0" 
      class="table is-fullwidth is-hoverable">
      <thead>
        <th 
          v-for="(header, headerIndex) in headers"
          :key="headerIndex">
          {{ header }}
        </th>
        <th></th>
      </thead>

      <tbody>
        <template v-for="(task, i) in tasks">
          <tr 
            class="v-centered"
            :key="task.id">
            <td>
              {{ task.production_step.master_production.project.project_name }}
            </td>
            <td>
              <div class="text--nowrap menu-label mb-0">
                {{ taskSource(task.production_step.master_production.part_type) }}
              </div>
              <div>
                {{ task.production_step.step.work_name }}
              </div>
            </td>
            <td>
              {{ task.qty_assigned }}
            </td>
            <td>
              {{ format(parse(task.started_on)) }}
            </td>
            <td>
              {{ format(parse(task.completed_on)) }}
            </td>
            <td>
              {{ task.paused_min_str }}
            </td>
            <td>
              {{ task.exp_duration_min_str }}
            </td>
            <td>
              {{ task.duration_min_str }}
            </td>
            <td>
              <div class="buttons buttons-right">
                <button 
                  v-if="i != 0"
                  @click="moveUp(i, task)"
                  class="button has-text-grey">
                  <span class="icon">
                    <i class="fas fa-chevron-up">
                    </i>
                  </span>
                </button>
                <button 
                  v-if="i != tasks.length - 1"
                  @click="moveDown(i, task)"
                  class="button has-text-grey">
                  <span class="icon">
                    <i class="fas fa-chevron-down">
                    </i>
                  </span>
                </button>
                <button 
                  @click="launchConfirmDelete(task.id)"
                  class="button has-text-grey ml-3">
                  <span class="icon">
                    <i class="fas fa-minus-circle">
                    </i>
                  </span>
                </button>
                <button 
                  :disabled="task.completed_on"
                  @click="markAsStarted(task.id)"
                  v-if="!task.is_in_progress"
                  class="button is-warning">
                  <span class="icon">
                    <i class="fas fa-play">
                    </i>
                  </span>
                </button>
                <button 
                  v-if="task.is_in_progress"
                  @click="pauseTask(task.id)"
                  class="button is-danger">
                  <span class="icon">
                    <i class="fas fa-pause">
                    </i>
                  </span>
                </button>
                <button 
                  :disabled="!!task.completed_on"
                  @click="markAsComplete(task.id)"
                  class="button is-success">
                  <span class="icon">
                    <i class="fas fa-check">
                    </i>
                  </span>
                </button>
              </div>
            </td>
          </tr>
        </template>
      </tbody>
    </table>

    <v-confirm-modal 
      v-model="confirmDelete.state"
      :title="confirmDelete.title"
      :message="confirmDelete.message"
      :type="confirmDelete.type"
      @confirm="deleteConfirmedCb()"
      @cancel="deleteId = null; confirmDelete.state = false"/>

    <!-- <mark-task-complete-modal
      :task="markComplete.task"
      v-model="markComplete.state"/> -->

  </div>
</template>

<script>
import VConfirmModal from '@/components/base/VConfirmModal'
import { mapState, mapActions, mapMutations } from 'vuex'

import { parseISO, format } from 'date-fns'

export default {
  components: { VConfirmModal },
  data() {
    return {
      headers: [
        'Project', 'Task', 'Quantity', 'Started On', 
        'Completed On', 'Paused Time', 'Exp. Duration', 'Actual Duration'
      ],
      confirmDelete: {
        state: false,
        title: 'Delete Confirmation',
        message: 'Are you sure you want to unassign this task from this workstation?',
        type: 'danger'
      },
      deleteConfirmedCb: null,
      parse: (d) => {
        return d ? parseISO(d) : null
      },
      format: (d) => {
        return d ? format(d, 'yyyy-MM-dd H:mm:ss') : null
      },
      markComplete: {
        task: null,
        state: false
      }
    }
  },
  computed: {
    ...mapState("workstations", [
      "workstations"
    ]),
    ...mapState("workstationQueues", [
      "tasks", "loading", "filters"
    ]),
    workstation() {
      return this.workstations.filter(
        d => d.id == this.filters.workstation_id
      )[0];
    }
  },
  watch: {
    'filters.workstation_id'(id) {
      this.getWorkstationTasks({ 
        workstation_id: id
      });
    }
  },
  created() {
    this.initialize();
  },
  methods: {
    ...mapActions("workstationQueues", [
      "getWorkstationTasks", "updateTask", "deleteWorkstationTask"
    ]),
    ...mapActions("workstations", [
      "getWorkstations"
    ]),
    ...mapMutations("workstationQueues", [
      "setFilter", "sortTasks"
    ]),
    initialize() {
      if (this.workstations.length == 0) {
        this.getWorkstations().then(() => 
          this.setFilter({
            field: 'workstation_id', 
            value: this.workstations[0].id
          })
        );
      } else {
        this.setFilter({
          field: 'workstation_id', 
          value: this.workstations[0].id
        });
      }
    },
    moveUp(i, task) {
      let prevOrder = this.tasks[i-1].workstation_order;
      this.updateTask({
        taskId: this.tasks[i-1].id,
        data: { workstation_order: task.workstation_order }
      }).then(
        () => {
          this.updateTask({
            taskId: task.id,
            data: { workstation_order: prevOrder }
          }).then(() => this.sortTasks());
        }
      )
    },
    moveDown(i, task) {
      let nextOrder = this.tasks[i+1].workstation_order;
      this.updateTask({
        taskId: this.tasks[i+1].id,
        data: { workstation_order: task.workstation_order }
      }).then(
        () => {
          this.updateTask({
            taskId: task.id,
            data: { workstation_order: nextOrder }
          }).then(() => this.sortTasks());
        }
      )
    },
    markAsStarted(taskId) {
      let now = new Date();

      this.updateTask({
        taskId: taskId,
        data: { started_on: now.toISOString() }
      })
    },
    pauseTask(taskId) {
      let now = new Date();

      this.updateTask({
        taskId: taskId,
        data: { is_in_progress: false }
      })
    },
    markAsComplete(taskId) {
      let now = new Date();

      this.updateTask({
        taskId: taskId,
        data: { completed_on: now.toISOString() }
      })
    },
    launchConfirmDelete(id) { 
      this.deleteConfirmedCb = () => {
        this.deleteWorkstationTask(id);
        this.confirmDelete.state = false;
      }

      this.confirmDelete.state = true;
    },
    taskSource(partType) {
      if (partType == 'production_sheet_item_id') {
        return 'Production Sheet'
      } else if (partType == 'cutsheetpart_id') {
        return 'Cut Sheet'
      } else if (partType == 'assemblysheet_id') {
        return 'Assembly Sheet'
      }
    }
  }
}
</script>

<style lang="scss" scoped>
@import '@/styles/table.scss';
</style>